import { IDynamicModel, IDynamicUpdateOnValueChange } from '../../models';
import { dynamicConflictDefaultDisplay } from '../../utils';

/**
 * The message a typeahead pushes into one `updateOnValueChange` target once the entry's value
 * resolves. Moved out of `DynamicTypeaheadBaseComponent._setSelectedItem` (WI 34965). A
 * calculated value of null with no `matchTargetProperty` has nothing to push, so it only clears
 * the target's loading state: a `value: null` push would reach the target's enable branch and
 * re-enable a disabled or read-only control (code-msi, PR 4505 thread 55952). A calculated empty
 * string (a lookup that found no value, e.g. a vendor with no contact) asks the target to clear a
 * stale auto-fill instead, which never enables it (Task 35033).
 */
export function dynamicFanOutTarget(
  selectionConfig: IDynamicUpdateOnValueChange,
  propertyValue: IDynamicModel['value'] | null,
  source: { formControlName?: string; label?: string },
  selectedItem: unknown
): IDynamicModel {
  const calculated = selectionConfig.calculatedPropertyValue != null;
  if (
    (propertyValue == null || propertyValue === '') &&
    calculated &&
    !selectionConfig.matchTargetProperty
  ) {
    return {
      formControlName: selectionConfig.formControlName,
      type: propertyValue === '' ? 'target-clear' : 'target-loading',
      value: 0,
    };
  }

  const targetModel: IDynamicModel = {
    formControlName: selectionConfig.formControlName,
    type: 'target',
    // null is pushed as before when a matchTargetProperty forces the search result.
    value: propertyValue as IDynamicModel['value'],
  };
  if (selectionConfig.matchTargetProperty) {
    targetModel.options = {
      typeahead: {
        matchTargetProperty: selectionConfig.matchTargetProperty,
        // force the target value to null if no result was found when using calculatedPropertyValue
        setNullValueIfNotFoundInSearch: calculated,
      },
      // tell the target which source (and value) triggered this auto-default, for the conflict popover wording
      conflictSource: {
        formControlName: source.formControlName,
        label: source.label,
        valueDisplay: dynamicConflictDefaultDisplay(selectedItem),
      },
    };
  }
  return targetModel;
}

/**
 * Which pick owns each fan-out target's loading flag (WI 34965). A pick takes the flag when its
 * lookup starts; only the owner may clear it, so an older lookup returning first cannot make the
 * form saveable while a newer lookup is still in flight (grok_code-ryn_3, PR 4505 thread 55972).
 */
export class DynamicFanOutLoading {
  private readonly owners = new Map<string, number>();

  start(target: string, pick: number): void {
    this.owners.set(target, pick);
  }

  /**
   * The update a resolved lookup pushes: the target value for the current pick, a loading clear
   * for an older pick that still owns the flag (the newer action started no lookup, e.g. a
   * clear), else null.
   */
  resolve(
    selectionConfig: IDynamicUpdateOnValueChange,
    pick: { pick: number; isCurrent: boolean },
    propertyValue: IDynamicModel['value'] | null,
    source: { formControlName?: string; label?: string },
    selectedItem: unknown
  ): IDynamicModel | null {
    const target = selectionConfig.formControlName as string;
    const owns = this.owners.get(target) === pick.pick;
    if (owns) {
      this.owners.delete(target);
    }
    if (pick.isCurrent) {
      return dynamicFanOutTarget(
        selectionConfig,
        propertyValue,
        source,
        selectedItem
      );
    }
    return owns
      ? { formControlName: target, type: 'target-loading', value: 0 }
      : null;
  }

  /**
   * WI 35277 (code-hr, PR 4855 thread 60724): a lookup that FAILED releases the target's loading
   * flag when its pick still owns it, so a lookup-gated form is not held unsaveable by a 500.
   * The target keeps its value; the user can fill it by hand.
   */
  settle(target: string, pick: number): IDynamicModel | null {
    if (this.owners.get(target) !== pick) {
      return null;
    }
    this.owners.delete(target);
    return { formControlName: target, type: 'target-loading', value: 0 };
  }
}
